/*******************************************************
* Library Description
* 
* [Brief 1-3 sentence description of the library's main purpose and functionality]
* 
* Key Features:
* - [Core capability 1]
* - [Core capability 2]
* - [Core capability 3]
* 
* Primary Operations:
* - functionName1(): [Brief description]
* - functionName2(): [Brief description]
* - utilityFunction(): [Brief description]
* 
* Usage:
* ```typescript
* // Basic usage example
* import { functionName1, functionName2 } from './libraryName';
* 
* // Example 1: Basic functionality
* const result = functionName1(param1, param2);
* 
* // Example 2: Advanced usage
* const data = await functionName2()
*   .pipe(utilityFunction())
*   .process();
* ```
********************************************************/
//########## IMPORTS ##########
//#region IMPORTS

import { type AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { type TrackerActivityHistoryRow, type TestPackageActivityHistoryRow } from './servicesLib';
import Bowser from 'bowser';
import * as schema from '@/src/db/models/MainSchema';
import { entityRelationships } from '@/src/db/models/EntityRelationships';
import { computeFAClaimedQuantity } from '@/src/scripts/libs/progFAClaimMath';
import { getMatchingTestPackageHistory, getPendingSAPercent } from './saHistoryLib';
import { clearCSRFToken, readCSRFToken } from './csrfTokenStore';

//#endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
export interface ServiceListResponse<TItem> {
    data: TItem[];
    recordsCount: number;
    page: number;
    pageSize: number;
    recordsTotal: number;
    pagesTotal: number;
    requestURL: string;
}

type LengthConstrainedColumn = {
    readonly length: number | undefined;
};

//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS
const commonEmailDomains = [
    // Performance Contractors Email Domains
    'performance-contractors.com',
    'performance.inc',
    // Other Common Email Domains
    'gmail.com',
    'yahoo.com',
    'outlook.com',
    'hotmail.com',
    'icloud.com',
    'aol.com',
    'msn.com',
    'comcast.net',
    'live.com',
    'ymail.com',
];
const CLIENT_METADATA_COLUMNS = {
    osName: [
        schema.Prog_SubmissionDetails.Os,
        schema.Prog_SubmissionDetailsREV.Os,
        schema.TS_ShiftDetails.Os,
        schema.TS_ShiftDetailsREV.Os,
    ],
    osVersion: [
        schema.Prog_SubmissionDetails.OsVersion,
        schema.Prog_SubmissionDetailsREV.OsVersion,
        schema.TS_ShiftDetails.OsVersion,
        schema.TS_ShiftDetailsREV.OsVersion,
    ],
    browserName: [
        schema.Prog_SubmissionDetails.Browser,
        schema.Prog_SubmissionDetailsREV.Browser,
        schema.TS_ShiftDetails.Browser,
        schema.TS_ShiftDetailsREV.Browser,
    ],
    browserVersion: [
        schema.Prog_SubmissionDetails.BrowserVersion,
        schema.Prog_SubmissionDetailsREV.BrowserVersion,
        schema.TS_ShiftDetails.BrowserVersion,
        schema.TS_ShiftDetailsREV.BrowserVersion,
    ],
} satisfies Record<string, readonly LengthConstrainedColumn[]>;

//#endregion


//########## FUNCTIONS ##########
//#region FUNCTIONS
function truncateClientMetadata(
    value: string,
    columns: readonly LengthConstrainedColumn[],
): string {
    const declaredLengths = columns
        .map(column => column.length)
        .filter((length): length is number => length !== undefined);

    if (declaredLengths.length === 0) return value;

    return Array.from(value).slice(0, Math.min(...declaredLengths)).join('');
}

/**
 * Attempts to get OS Name and Version without external libraries.
 * Tries navigator.userAgentData first, then falls back to parsing navigator.userAgent.
 *
 * WARNING: User Agent string parsing is unreliable and hard to maintain.
 * This is a simplified example and may not be accurate for all browsers/OS versions.
 *
 * @returns {Promise<{ name: string, version: string }>} A Promise resolving to an object { name: string, version: string }
 */

async function getOs(): Promise<{ name: string; version: string }> {
    let osName = 'Unknown OS';
    let osVersion = 'Unknown Version';

    // --- 1. Try navigator.userAgentData (Modern & Preferred) ---
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    if ((navigator as any).userAgentData) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        const uaData = (navigator as any).userAgentData;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        osName = uaData.platform || osName; // e.g., "Windows", "macOS", "Android"

        try {
            // platformVersion is a "high entropy" value, requires explicit request in secure context
            // This might fail or return a less specific version than the UA string
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            const highEntropyData = await uaData.getHighEntropyValues(['platformVersion']);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (highEntropyData.platformVersion) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                osVersion = highEntropyData.platformVersion;
            }
            // If we got at least the platform name, return this result.
            // A more complex logic could still fall back to UA parsing *just* for version if needed.
            if (osName !== 'Unknown OS') {
                return {
                    name: truncateClientMetadata(osName, CLIENT_METADATA_COLUMNS.osName),
                    version: truncateClientMetadata(osVersion, CLIENT_METADATA_COLUMNS.osVersion),
                };
            }
        } catch (error) {
            console.warn("Could not get platformVersion from userAgentData (might require secure context or permissions), falling back to UA string parsing. Error:", error);
            // Fall through to UA string parsing below
        }
    }

    // --- 2. Fallback: Parse navigator.userAgent (Classic & Fragile) ---
    const ua = navigator.userAgent;

    try {
        // Windows
        if (ua.includes("Windows NT")) {
            osName = "Windows";
            const match = /Windows NT (\d+\.\d+)/.exec(ua);
            if (match?.[1]) {
                const ntVersion = match[1];
                // Map NT versions to common Windows version names
                switch (ntVersion) {
                    case "10.0": osVersion = "10 / 11"; break; // Note: Cannot reliably distinguish 10 vs 11 via NT version alone
                    case "6.3": osVersion = "8.1"; break;
                    case "6.2": osVersion = "8"; break;
                    case "6.1": osVersion = "7"; break;
                    case "6.0": osVersion = "Vista"; break;
                    case "5.1": osVersion = "XP"; break;
                    case "5.0": osVersion = "2000"; break;
                    default: osVersion = `NT ${ntVersion}`;
                }
            }
        }
        // Mac OS X / macOS
        else if (ua.includes("Macintosh") || ua.includes("Mac OS X")) {
            osName = "macOS";
            // Try finding version like "Mac OS X 10_15_7" or sometimes within "Version/X.Y.Z" for Safari
            const match = /Mac OS X (\d+[._]\d+[._]?\d*)/.exec(ua);
            if (match?.[1]) {
                osVersion = match[1].replace(/_/g, '.');
            } else {
                // As a fallback, sometimes Safari version approximates macOS version
                const safariVersionMatch = /Version\/(\d+\.\d+(\.\d+)?)/.exec(ua);
                if (safariVersionMatch?.[1]) {
                    // This is less reliable, map known Safari versions to macOS versions if needed
                    osVersion = `Unknown (Safari ${safariVersionMatch[1]})`; // Indicate it's less certain
                }
            }
        }
        // iOS (iPhone, iPad)
        else if (ua.includes("iPhone") || ua.includes("iPad")) {
            osName = "iOS";
            const match = /OS (\d+[._]\d+[._]?\d*)/.exec(ua);
            if (match?.[1]) {
                osVersion = match[1].replace(/_/g, '.');
            }
        }
        // Android
        else if (ua.includes("Android")) {
            osName = "Android";
            const match = /Android (\d+\.\d+(\.\d+)?)/.exec(ua);
            if (match?.[1]) {
                osVersion = match[1];
            }
        }
        // Linux
        else if (ua.includes("Linux")) {
            osName = "Linux";
            // Linux distribution/version is usually not in the standard UA string
            osVersion = "Unknown"; // It might contain kernel version, but not distro version
        }
        // Add checks for Chrome OS, other OS...
        else if (ua.includes("CrOS")) {
            osName = "Chrome OS";
            // Version often looks like x86_64 followed by a number, e.g., CrOS x86_64 14588.98.0
            const match = /CrOS [^\s]+ (\d+\.\d+\.\d+)/.exec(ua);
            if (match?.[1]) {
                osVersion = match[1];
            }
        }

        // If still unknown, check deprecated navigator.platform as a last resort
        if (osName === 'Unknown OS' && navigator.platform) {
            const platform = navigator.platform.toUpperCase();
            if (platform.includes('WIN')) osName = 'Windows';
            else if (platform.includes('MAC')) osName = 'macOS';
            else if (platform.includes('LINUX')) osName = 'Linux';
            else if (platform.includes('IPHONE') || platform.includes('IPAD') || platform.includes('IPOD')) osName = 'iOS';
            else if (platform.includes('ANDROID')) osName = 'Android';
        }

    } catch (err) {
        console.error("Error during User Agent parsing:", err);
        // Keep osName/osVersion as 'Unknown' if parsing fails
    }

    return {
        name: truncateClientMetadata(osName, CLIENT_METADATA_COLUMNS.osName),
        version: truncateClientMetadata(osVersion, CLIENT_METADATA_COLUMNS.osVersion),
    };
}

/**
 * Gets the browser name and version using the Bowser library.
 * This provides reliable browser detection across all major browsers.
 *
 * @returns {Promise<{ name: string, version: string }>} A Promise resolving to an object { name: string, version: string }
 */
async function getBrowser(): Promise<{ name: string; version: string }> {
    try {
        const browser = Bowser.getParser(window.navigator.userAgent);
        const browserInfo = browser.getBrowser();

        return {
            name: truncateClientMetadata(browserInfo.name || 'Unknown Browser', CLIENT_METADATA_COLUMNS.browserName),
            version: truncateClientMetadata(browserInfo.version || 'Unknown Version', CLIENT_METADATA_COLUMNS.browserVersion)
        };
    } catch (error) {
        console.error('Error detecting browser:', error);
        return {
            name: truncateClientMetadata('Unknown Browser', CLIENT_METADATA_COLUMNS.browserName),
            version: truncateClientMetadata('Unknown Version', CLIENT_METADATA_COLUMNS.browserVersion)
        };
    }
}

/**
 * Gets the user's current location using the browser's Geolocation API
 *
 * @param options - Optional configuration for the geolocation request.
 *   `deadlineMs` caps the whole wait, including time the permission prompt sits unanswered,
 *   which the browser's own `timeout` does not count.
 * @returns Promise resolving to coordinates object with latitude and longitude strings
 */
async function getLocation(options?: {
    enableHighAccuracy?: boolean;
    timeout?: number;
    maximumAge?: number;
    deadlineMs?: number;
}): Promise<{ latitude: string; longitude: string }> {
    const { deadlineMs = 15000, ...positionOptions } = options ?? {};
    return new Promise((resolve, reject) => {
        // Check if geolocation is supported
        if (!navigator.geolocation) {
            reject(new Error('Geolocation is not supported by this browser'));
            return;
        }

        // Default options
        const defaultOptions: PositionOptions = {
            enableHighAccuracy: true,
            timeout: 10000, // 10 seconds
            maximumAge: 300000, // 5 minutes
            ...positionOptions
        };

        // An unanswered permission prompt never calls back, so give up on our own clock.
        const deadline = setTimeout(() => reject(new Error('Location request timed out')), deadlineMs);

        navigator.geolocation.getCurrentPosition(
            (position) => {
                clearTimeout(deadline);
                const { latitude, longitude } = position.coords;
                resolve({
                    latitude: latitude.toString(),
                    longitude: longitude.toString()
                });
            },
            (error) => {
                clearTimeout(deadline);
                reject(new Error(error.message || 'Unable to retrieve location'));
            },
            defaultOptions
        );
    });
}

async function logout(router?: AppRouterInstance) {
    console.log('[LOGOUT BOUNCE] logout() called', new Error().stack);
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 1000; // 1 second delay

    let lastError: Error | null = null;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
            const csrfToken = readCSRFToken();
            const response = await fetch('/api/v1/Auth/Logout', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'CSRFToken': csrfToken ?? '',
                },
                credentials: 'include',
            });

            if (response.ok) {
                clearCSRFToken();
                if (router && typeof router.push === 'function') {
                    router.push('/Login');
                } else {
                    window.location.href = '/Login'; // Fallback
                }
                return;
            } else {
                lastError = new Error(`Logout API call failed on attempt ${attempt + 1}/${MAX_RETRIES}. Status: ${response.status} ${response.statusText}`);
                console.error(lastError.message);
            }
        } catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error));
            console.error(`Network or other error during logout attempt ${attempt + 1}/${MAX_RETRIES}:`, lastError);
        }

        if (attempt < MAX_RETRIES - 1) {
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
        }
    }

    console.error('Logout failed after all retries.', lastError ? lastError.message : "Unknown reason.");


    clearCSRFToken();
    if (router && typeof router.push === 'function') {
        router.push('/Login');
    } else {
        window.location.href = '/Login'; // Fallback
    }
}

async function callEmailService(payload: {
    to: string | string[];
    subject: string;
    body: string; // HTML email content
    from?: string;
    cc?: string | string[];
    bcc?: string | string[];
    attachments?: {
        filename: string;
        content: string; // base64 encoded
    }[];
}): Promise<boolean> {
    try {
        const csrfToken = readCSRFToken();

        if (!csrfToken) {
            console.error('Email service request blocked: no in-memory CSRF token is available.');
            return false;
        }

        const response = await fetch('/api/v1/Services/EmailService', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'CSRFToken': csrfToken,
            },
            body: JSON.stringify(payload),
        });

        if (response.ok) {
            return true;
        } else {
            let errorDetails = `Status: ${response.status}`;
            try {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                const errorData = await response.json();
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                errorDetails += `, Error: ${errorData.error || response.statusText}`;
            } catch {
                // If response body is not JSON or empty
                errorDetails += `, StatusText: ${response.statusText}`;
            }
            console.error(`Failed to call email service. ${errorDetails}`);
            return false;
        }
    } catch (error) {
        // Catches network errors or other exceptions during the fetch operation
        console.error('Exception occurred while calling email service:', error);
        return false;
    }
}

async function convertImageColorToTheme(dataUrl: string, targetCssColor: string): Promise<string> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return dataUrl;
    }

    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return dataUrl;
    tempCtx.fillStyle = targetCssColor;
    const normalizedHex = typeof tempCtx.fillStyle === 'string' ? tempCtx.fillStyle : targetCssColor;

    const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
        const match = /^#([\da-f]{6})$/i.exec(hex);
        if (!match) return null;
        const intVal = parseInt(match[1], 16);
        return { r: (intVal >> 16) & 255, g: (intVal >> 8) & 255, b: intVal & 255 };
    };

    const rgb = hexToRgb(normalizedHex);
    if (!rgb) return dataUrl;

    const mimeMatch = /^data:(image\/[a-zA-Z0-9.+-]+);/i.exec(dataUrl);
    const outputMime = mimeMatch?.[1] ?? 'image/png';

    // SVG: recolor via string replacement instead of canvas pixel manipulation
    if (outputMime === 'image/svg+xml') {
        const base64 = dataUrl.split(',')[1];
        if (!base64) return dataUrl;
        try {
            let svg = atob(base64);
            svg = svg.replace(/stroke="[^"]*"/g, `stroke="${normalizedHex}"`);
            svg = svg.replace(/fill="(?!none)[^"]*"/g, `fill="${normalizedHex}"`);
            return `data:image/svg+xml;base64,${btoa(svg)}`;
        } catch {
            return dataUrl;
        }
    }

    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = document.createElement('img');
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Failed to load image'));
        image.src = dataUrl;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUrl;
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);

    try {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
            const alpha = data[i + 3];
            if (alpha !== 0) {
                data[i] = rgb.r;
                data[i + 1] = rgb.g;
                data[i + 2] = rgb.b;
            }
        }
        ctx.putImageData(imageData, 0, 0);
        return canvas.toDataURL(outputMime);
    } catch {
        return dataUrl;
    }
}

// takes a string and looks for X sized chunks without coming across a newline character. If we exceed X we add a new line at the next space
function wrapString(str: string, maxChunkSize: number): string {
    const wiggleRoom = 5;
    // Try to find a space between (maxChunkSize - wiggleRoom) and (maxChunkSize + wiggleRoom)
    const minIdx = Math.max(0, maxChunkSize - wiggleRoom);
    const maxIdx = Math.min(str.length - 1, maxChunkSize + wiggleRoom);

    let splitIdx = -1;

    // Look forward for the nearest space within the wiggle range
    for (let i = maxChunkSize; i <= maxIdx; i++) {
        if (str[i] === ' ') {
            splitIdx = i;
            break;
        }
    }
    // If not found, look backward
    if (splitIdx === -1) {
        for (let i = maxChunkSize - 1; i >= minIdx; i--) {
            if (str[i] === ' ') {
                splitIdx = i;
                break;
            }
        }
    }
    // If still not found, fall back to the default behavior (look for any space after maxChunkSize)
    if (splitIdx === -1) {
        splitIdx = str.indexOf(' ', maxChunkSize);
    }
    // If no spaces at all, return original string
    if (splitIdx === -1) {
        return str;
    }
    return str.substring(0, splitIdx) + '\n' + wrapString(str.substring(splitIdx + 1), maxChunkSize);
}

function resolvePath<T>(obj: Record<string, unknown>, path: string): T | undefined {
    return path.split(".").reduce<unknown>((o, k) => (o as Record<string, unknown>)?.[k], obj) as T ?? undefined;
}

/**
 * Calculates the remaining quantity from TakeOffQuantity and activity history EarnedValue.
 * Formula: ROUND((1 - SUM(EarnedValue where QueID=3)) * TakeOffQuantity, 2)
 *
 * Pass rocID to calculate availability for one assigned Rule of Credit.
 *
 * @param trackerMeta - TrackerMeta object with ID, DisciplineID, TakeOffQuantity, ActivityTypeID
 * @param activityHistory - Activity history records with EarnedValue from TrackerActivityHistory service
 * @param options - Filtering options
 * @param options.rocID - Filter to a specific Rule of Credit
 * @returns The remaining quantity value (number)
 *
 * @example
 * // Basic usage for non-ROC
 * const remaining = getRemainingQuantity(row.Tracker, row.ActivityHistory);
 *
 * @example
 * // ROC - get remaining for a specific ROC
 * const remainingForRoc = getRemainingQuantity(row.Tracker, row.ActivityHistory, { rocID: rocItem.rocID });
 */
function getRemainingQuantity(
    trackerMeta: {
        ID?: number;
        DisciplineID: number;
        TakeOffQuantity?: string | number | null;
        ActivityTypeID?: number | null;
    } | null | undefined,
    activityHistory: TrackerActivityHistoryRow[] = [],
    options?: { rocID?: number | null }
): number {
    if (!trackerMeta) return 0;

    const { ID: trackerID, TakeOffQuantity } = trackerMeta;

    return calculateQuantityRemaining(TakeOffQuantity ?? null, activityHistory, {
        trackerID,
        rocID: options?.rocID,
    });
}

/**
 * Calculates total earned value for a tracker from activity history records.
 * Only includes earned items (QueID = 3).
 *
 * @param historyRecords - Array of TrackerActivityHistoryRow with EarnedValue
 * @param options - Filtering options
 * @param options.trackerID - Filter to specific tracker
 * @param options.rocID - Filter to a specific Rule of Credit
 * @returns Total earned value (0-1 decimal representing percentage)
 */
function calculateTotalEarnedValue(
    historyRecords: TrackerActivityHistoryRow[],
    options?: {
        trackerID?: number | null;
        rocID?: number | null;
    }
): number {
    if (!historyRecords || historyRecords.length === 0) return 0;

    return historyRecords.reduce((total, record) => {
        // Filter by TrackerID if specified
        if (options?.trackerID != null && record.TrackerID !== options.trackerID) {
            return total;
        }

        if (options?.rocID != null && record.ROCID !== options.rocID) {
            return total;
        }

        // Only QueID 3 means "earned" - include in EarnedValue calculation
        if (record.QueID !== 3) {
            return total;
        }

        return total + Number(record.EarnedValue ?? 0);
    }, 0);
}

/**
 * Calculates quantity remaining from TakeOffQuantity and EarnedValue history.
 * Formula: ROUND((1 - TotalEarnedValue) * TakeOffQuantity, 2)
 *
 * @param takeOffQuantity - The original takeoff quantity from Prog_TrackerMeta
 * @param historyRecords - Activity history with EarnedValue
 * @param options - Filtering options for earned value calculation
 * @returns Remaining quantity rounded to 2 decimal places
 */
function calculateQuantityRemaining(
    takeOffQuantity: number | string | null,
    historyRecords: TrackerActivityHistoryRow[],
    options?: {
        trackerID?: number | null;
        rocID?: number | null;
    }
): number {
    const toq = Number(takeOffQuantity ?? 0);
    if (toq === 0) return 0;

    const totalEarned = calculateTotalEarnedValue(historyRecords, options);
    const remaining = (1 - totalEarned) * toq;

    // Round to 2 decimal places
    return Math.round(remaining * 100) / 100;
}

/**
 * Calculates the pending quantity from a list of tracker activity history records.
 * Filters out earned items (QueID >= 3) to only include items that are actually pending.
 * 
 * QueID meanings:
 * - 1: SuperReview (pending)
 * - 2: PSR/Project Services Review (pending)
 * - 3: Earned (not pending - excluded)
 * - 4+: Invalid/other states (excluded)
 * 
 * @param historyRecords - Array of TrackerActivityHistoryRow
 * @param options - Optional filtering options
 * @param options.trackerID - Filter to only records matching this TrackerID
 * @param options.excludeRecordNumber - Exclude a specific RecordNumber (e.g., the current FA being viewed)
 * @param options.rocID - Filter to only records matching this Rule of Credit
 * @returns The sum of (Quantity * Percent) for all pending items
 * 
 * @example
 * // Basic usage - all pending items for a tracker
 * const pending = getPendingQuantity(historyData, { trackerID: row.TrackerID });
 * 
 * @example
 * // Exclude current record when viewing an FA
 * const pending = getPendingQuantity(historyData, { 
 *     trackerID: row.TrackerID, 
 *     excludeRecordNumber: row.RecordNumber 
 * });
 * 
 * @example
 * // Filter by Rule of Credit
 * const pending = getPendingQuantity(historyData, { 
 *     trackerID: tracker.ID, 
 *     rocID
 * });
 */
function getPendingQuantity(
    historyRecords: TrackerActivityHistoryRow[],
    options?: {
        trackerID?: number | null;
        excludeRecordNumber?: number | null;
        rocID?: number | null;
        takeOffQuantity?: number;
    }
): number {
    if (!historyRecords || historyRecords.length === 0) return 0;

    return historyRecords.reduce((total, record) => {
        // Filter by TrackerID if specified (treat null as "not specified")
        if (options?.trackerID != null && record.TrackerID !== options.trackerID) {
            return total;
        }

        // Exclude specific RecordNumber if specified (treat null as "not specified")
        if (options?.excludeRecordNumber != null && record.RecordNumber === options.excludeRecordNumber) {
            return total;
        }

        if (options?.rocID != null && record.ROCID !== options.rocID) {
            return total;
        }

        // Only QueID 1 (SuperReview) and QueID 2 (PSR) are pending
        if (record.QueID !== 1 && record.QueID !== 2) {
            return total;
        }

        return total + computeFAClaimedQuantity(
            record.Quantity,
            record.Percent,
            options?.takeOffQuantity
        );
    }, 0);
}

/**
 * Calculates the pending percent from a list of tracker activity history records.
 * Sums raw Percent values for pending records (QueID 1, 2).
 * Mirrors getPendingQuantity filtering but returns sum of record.Percent (0-1 decimal).
 *
 * @param historyRecords - Array of TrackerActivityHistoryRow
 * @param options - Optional filtering options
 * @param options.trackerID - Filter to only records matching this TrackerID
 * @param options.excludeRecordNumber - Exclude a specific RecordNumber (e.g., the current FA being viewed)
 * @returns The sum of Percent for all pending items (0-1 decimal)
 */
function getPendingPercent(
    historyRecords: TrackerActivityHistoryRow[],
    options?: {
        trackerID?: number | null;
        excludeRecordNumber?: number | null;
        rocID?: number | null;
    }
): number {
    if (!historyRecords || historyRecords.length === 0) return 0;

    return historyRecords.reduce((total, record) => {
        if (options?.trackerID != null && record.TrackerID !== options.trackerID) {
            return total;
        }

        if (options?.excludeRecordNumber != null && record.RecordNumber === options.excludeRecordNumber) {
            return total;
        }

        if (options?.rocID != null && record.ROCID !== options.rocID) {
            return total;
        }

        // Only QueID 1 (SuperReview) and QueID 2 (PSR) are pending
        if (record.QueID !== 1 && record.QueID !== 2) {
            return total;
        }

        return total + Number(record.Percent ?? 0);
    }, 0);
}

/**
 * Checks if an FA record is overclaimed (quantity exceeds remaining).
 * Used to determine yellow/warning state for FA cells and rows.
 *
 * @param row - FA record with Tracker data including TakeOffQuantity
 * @param trackerHistoryData - History records for pending calculation and EarnedValue
 * @param exceptionStatuses - Optional array of StatusIDs that also trigger warning (e.g., PSR Returned)
 * @returns true if overclaimed or has exception status
 */
function isFAOverclaimed(
    row: {
        Quantity?: number | string | null;
        Percent?: number | string | null;
        ROCID?: number | null;
        TrackerID?: number | null;
        RecordNumber?: number | null;
        StatusID?: number | null;
        Tracker?: {
            ID?: number;
            DisciplineID: number;
            TakeOffQuantity?: string | number | null;
            ActivityTypeID?: number | null;
        } | null;
    },
    trackerHistoryData: TrackerActivityHistoryRow[],
    exceptionStatuses: number[] = []
): boolean {
    // Check exception status first
    if (row.StatusID != null && exceptionStatuses.includes(row.StatusID)) {
        return true;
    }

    const claimTotal = computeFAClaimedQuantity(
        row.Quantity,
        row.Percent,
        row.Tracker?.TakeOffQuantity
    );
    // Calculate remaining quantity from activity history EarnedValue
    const quantityRemaining = getRemainingQuantity(row.Tracker, trackerHistoryData, { rocID: row.ROCID });
    const pendingQuantity = getPendingQuantity(trackerHistoryData, {
        trackerID: row.TrackerID,
        excludeRecordNumber: row.RecordNumber,
        rocID: row.ROCID,
        takeOffQuantity: Number(row.Tracker?.TakeOffQuantity ?? 0),
    });
    const totalClaimed = claimTotal + pendingQuantity;
    return totalClaimed > quantityRemaining;
}

/**
 * Checks if an SA record is overclaimed (percent exceeds 100% when combined with pending).
 * Used to determine yellow/warning state for SA cells and rows.
 * 
 * @param row - Sell Activity record with its Bulk Activity match target and RoC label
 * @param testPackageHistoryData - History records for pending calculation
 * @param exceptionStatuses - Optional array of StatusIDs that also trigger warning
 * @returns true if overclaimed or has exception status
 */
function isSAOverclaimed(
    row: {
        DisciplineID?: number | null;
        Percent?: number | string | null;
        MatchValue?: string | null;
        ROCLabelShort?: string | null;
        RecordNumber?: number | null;
        StatusID?: number | null;
    },
    testPackageHistoryData: TestPackageActivityHistoryRow[],
    exceptionStatuses: number[] = []
): boolean {
    // Check exception status first
    if (row.StatusID != null && exceptionStatuses.includes(row.StatusID)) {
        return true;
    }

    const claimPercent = Number(row.Percent ?? 0);
    const testPackageHistory = getMatchingTestPackageHistory(row, testPackageHistoryData);
    if (!testPackageHistory) return true;

    // Check if claim + pending exceeds 100% (1.0)
    const pendingPercent = getPendingSAPercent(testPackageHistory);
    const totalClaimed = claimPercent + pendingPercent;
    return totalClaimed > 1.0;
}

/**
 * Checks if a ROC rollup is overclaimed.
 * A rollup is overclaimed if any Tracker Item and Rule of Credit pair exceeds available quantity.
 * 
 * @param rollup - ROC rollup containing FAs grouped by tracker and date
 * @param trackerHistoryData - History records for pending calculation
 * @param exceptionStatuses - Optional array of StatusIDs that trigger warning
 * @returns true if any Rule of Credit is overclaimed or any FA has exception status
 */
function isRocRollupOverclaimed(
    rollup: {
        trackerID?: number | null;
        fas: Array<{
            TrackerID?: number | null;
            ROCID?: number | null;
            RecordNumber?: number | null;
            Quantity?: number | string | null;
            Percent?: number | string | null;
            StatusID?: number | null;
            Tracker?: { TakeOffQuantity?: number | string | null } | null;
        }>;
    },
    trackerHistoryData: TrackerActivityHistoryRow[],
    exceptionStatuses: number[] = []
): boolean {
    const fas = rollup.fas;
    if (!fas || fas.length === 0) return false;

    // Check if any FA has exception status
    if (fas.some(fa => fa.StatusID != null && exceptionStatuses.includes(fa.StatusID))) {
        return true;
    }

    // Group by Tracker Item and Rule of Credit. Grouped rosters may use one
    // Tracker Item per claimable item, but share the same identity model.
    const claimGroups = new Map<string, {
        trackerID: number | null;
        rocID: number | null;
        takeOffQuantity: number;
        fas: typeof fas;
    }>();
    for (const fa of fas) {
        const trackerID = fa.TrackerID ?? rollup.trackerID ?? null;
        const rocID = fa.ROCID ?? null;
        const key = `${trackerID ?? 'unknown'}:${rocID ?? 'null'}`;
        const group = claimGroups.get(key) ?? {
            trackerID,
            rocID,
            takeOffQuantity: Number(fa.Tracker?.TakeOffQuantity ?? 0),
            fas: [],
        };
        group.fas.push(fa);
        claimGroups.set(key, group);
    }

    for (const { trackerID, rocID, takeOffQuantity, fas: typeFAs } of claimGroups.values()) {
        const currentQuantity = typeFAs.reduce((sum, fa) => (
            sum + computeFAClaimedQuantity(fa.Quantity, fa.Percent, fa.Tracker?.TakeOffQuantity ?? takeOffQuantity)
        ), 0);

        // Historical claims for this ROC item and tracker (exclude current rollup FAs)
        const historicalQuantity = trackerHistoryData
            .filter(h =>
                h.TrackerID === trackerID &&
                (rocID == null || h.ROCID === rocID) &&
                (h.QueID === 1 || h.QueID === 2) && // Only pending
                !typeFAs.some(fa => Number(h.RecordNumber) === Number(fa.RecordNumber))
            )
            .reduce((sum, h) => (
                sum + computeFAClaimedQuantity(h.Quantity, h.Percent, takeOffQuantity)
            ), 0);

        // remaining = (1 - earned) * takeOff — matches isFAOverclaimed pattern
        const remaining = calculateQuantityRemaining(takeOffQuantity, trackerHistoryData, {
            trackerID,
            rocID,
        });
        if (currentQuantity + historicalQuantity > remaining) {
            return true;
        }
    }

    return false; // No overclaims found
}

// Cache for resolved paths (schema is static)
const schemaPathCache = new Map<string, string | null>();

/**
 * Helper to get table name from drizzle schema object
 */
function getTableNameFromSchema(table: unknown): string | null {
    if (table && typeof table === 'object') {
        for (const [name, schemaTable] of Object.entries(schema)) {
            if (schemaTable === table) return name;
        }
    }
    return null;
}

/**
 * Resolves a field name to its dot-notation path from a starting table.
 * Uses BFS to find shortest path through oneToOne relations only.
 *
 * @param startTableName - Table to start from (e.g., 'Prog_FA')
 * @param targetField - Column name to find (e.g., 'Area')
 * @param maxDepth - Max traversal depth (default: 3)
 * @returns Resolved path (e.g., 'Tracker.Area') or original field if direct, or null if not found
 */
/**
 * Shared BFS over EntityRelationships. Traverses oneToOne and oneToMany
 * relations, optionally including view-backed relations, and calls `isMatch`
 * at each child table.
 * Returns the dot-notation path to the first match, or null.
 */
function bfsRelationPath(
    startTableName: string,
    isMatch: (childTableName: string, path: string[], relName: string) => string | null,
    maxDepth = 3,
    includeViewRelations = false
): string | null {
    const visited = new Set<string>([startTableName]);
    const queue: Array<{ tableName: string; path: string[]; depth: number }> = [{
        tableName: startTableName,
        path: [],
        depth: 0
    }];

    while (queue.length > 0) {
        const current = queue.shift()!;
        if (current.depth >= maxDepth) continue;

        const tableRels = entityRelationships[current.tableName];
        if (!tableRels) continue;

        for (const rel of tableRels.relations) {
            if (rel.relationType !== 'oneToOne' && rel.relationType !== 'oneToMany') continue;
            if (!includeViewRelations && rel.isView) continue;

            const childTableName = getTableNameFromSchema(rel.childTable);
            if (!childTableName) continue;

            const match = isMatch(childTableName, current.path, rel.name);
            if (match !== null) return match;

            if (!visited.has(childTableName)) {
                visited.add(childTableName);
                queue.push({
                    tableName: childTableName,
                    path: [...current.path, rel.name],
                    depth: current.depth + 1
                });
            }
        }
    }

    return null;
}

function resolveSchemaFieldPath(
    startTableName: string,
    targetField: string,
    maxDepth = 3,
    includeViewRelations = false
): string | null {
    const cacheKey = `${startTableName}:${targetField}:${includeViewRelations ? 'views' : 'tables'}`;
    if (schemaPathCache.has(cacheKey)) {
        return schemaPathCache.get(cacheKey) ?? null;
    }

    // Check direct column first
    const startTable = schema[startTableName as keyof typeof schema];
    if (startTable && typeof startTable === 'object' && targetField in startTable) {
        schemaPathCache.set(cacheKey, targetField);
        return targetField;
    }

    const result = bfsRelationPath(startTableName, (childTableName, path, relName) => {
        const childTable = schema[childTableName as keyof typeof schema];
        if (childTable && typeof childTable === 'object' && targetField in childTable) {
            return [...path, relName, targetField].join('.');
        }
        return null;
    }, maxDepth, includeViewRelations);

    schemaPathCache.set(cacheKey, result);
    return result;
}

/**
 * Batch resolve multiple fields efficiently.
 */
function resolveSchemaFieldPaths(
    startTableName: string,
    targetFields: string[],
    maxDepth = 3,
    includeViewRelations = false
): Map<string, string> {
    const results = new Map<string, string>();
    for (const field of targetFields) {
        const resolved = resolveSchemaFieldPath(startTableName, field, maxDepth, includeViewRelations);
        if (resolved) results.set(field, resolved);
    }
    return results;
}

// Cache for table-specific path resolution
const schemaPathWithTableCache = new Map<string, string | null>();

/**
 * Resolves a field name to its dot-notation path, targeting a specific table.
 * Unlike resolveSchemaFieldPath which finds the first matching table,
 * this finds the path to a specific target table.
 *
 * @param startTableName - Table to start from (e.g., 'Prog_FA', 'Prog_TrackerMeta')
 * @param targetField - Column name to find (e.g., 'LineNumber', 'PieceMark')
 * @param targetTableName - Specific table to find the field on (e.g., 'Prog_TrackerSpecsPipe')
 * @param maxDepth - Max traversal depth (default: 3)
 * @returns Resolved path (e.g., 'Prog_TrackerSpecsPipe.LineNumber') or null if not found
 *
 * @example
 * // From Prog_TrackerMeta to Prog_TrackerSpecsPipe
 * resolveSchemaFieldPathWithTable('Prog_TrackerMeta', 'LineNumber', 'Prog_TrackerSpecsPipe')
 * // Returns: 'Prog_TrackerSpecsPipe.LineNumber'
 *
 * @example
 * // From Prog_FA via Tracker to Prog_TrackerSpecsPipe
 * resolveSchemaFieldPathWithTable('Prog_FA', 'LineNumber', 'Prog_TrackerSpecsPipe')
 * // Returns: 'Tracker.Prog_TrackerSpecsPipe.LineNumber'
 */
function resolveSchemaFieldPathWithTable(
    startTableName: string,
    targetField: string,
    targetTableName: string,
    maxDepth = 3,
    includeViewRelations = false
): string | null {
    const cacheKey = `${startTableName}:${targetField}:${targetTableName}:${includeViewRelations ? 'views' : 'tables'}`;
    if (schemaPathWithTableCache.has(cacheKey)) {
        return schemaPathWithTableCache.get(cacheKey) ?? null;
    }

    // Check if we're already at the target table
    if (startTableName === targetTableName) {
        const startTable = schema[startTableName as keyof typeof schema];
        if (startTable && typeof startTable === 'object' && targetField in startTable) {
            schemaPathWithTableCache.set(cacheKey, targetField);
            return targetField;
        }
        schemaPathWithTableCache.set(cacheKey, null);
        return null;
    }

    const result = bfsRelationPath(startTableName, (childTableName, path, relName) => {
        if (childTableName !== targetTableName) return null;

        if (targetField.includes('.')) {
            // Dot-notation field (e.g., 'Foreman.FullName') — skip column validation,
            // the server validates the full path via getNestedColumnForPath
            return [...path, relName, targetField].join('.');
        }
        const childTable = schema[childTableName as keyof typeof schema];
        if (childTable && typeof childTable === 'object' && targetField in childTable) {
            return [...path, relName, targetField].join('.');
        }
        return null;
    }, maxDepth, includeViewRelations);

    schemaPathWithTableCache.set(cacheKey, result);
    return result;
}
//#endregion


//########## EXPORTS ##########
//#region EXPORTS
export {
    getOs,
    getBrowser,
    logout,
    callEmailService,
    getLocation,
    convertImageColorToTheme,
    wrapString,
    resolvePath,
    getRemainingQuantity,
    calculateTotalEarnedValue,
    calculateQuantityRemaining,
    getPendingQuantity,
    getPendingPercent,
    isFAOverclaimed,
    isSAOverclaimed,
    isRocRollupOverclaimed,
    resolveSchemaFieldPath,
    resolveSchemaFieldPaths,
    resolveSchemaFieldPathWithTable,
    commonEmailDomains,
}
//#endregion
